<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue'
import { fetchPanelMatches, fetchPanelTypes } from '../lib/api.js'
import ValueSlider from './ValueSlider.vue'
import ActionButton from './ActionButton.vue'
import {
  readDefaultsOverrideFromLocation,
  resolveCalculatorDefaults,
  resolveSystemTemps,
  resolveTargetWattage,
} from '../lib/calculator-defaults.js'

const props = defineProps({
  productId: String,
  product: Object,
  category: String,
  market: String,
  lang: String,
  strings: Object,
  showEudop: [Boolean, String],
  hideColumns: String,
})

// Same outer spacing as PanelComponent: page margins only when standalone, none when embedded
const isEmbedded = ref(false)
onMounted(() => {
  const el = getCurrentInstance()?.proxy?.$el
  isEmbedded.value = el?.getRootNode() instanceof ShadowRoot
})

const lockDimension = ref('type')
const lockedValue = ref(null)
// Slider limits. Until the types endpoint answers (or when it gives none) these apply; products
// such as the Ulow series report narrower heating limits there.
const DEFAULT_TEMP_BOUNDS = {
  flowMin: 30,
  flowMax: 90,
  returnMin: 20,
  returnMax: 80,
  roomMin: 15,
  roomMax: 30,
}
const tempBounds = ref({ ...DEFAULT_TEMP_BOUNDS })
const clampTemp = (value, min, max) => Math.min(Math.max(value, min), max)

// Initial temperatures depend on the market (e.g. AustriaVN starts at 75/65/20) and are kept
// inside the product's limits
const marketTemps = computed(() => resolveSystemTemps(props.market))
const flowTemp = ref(marketTemps.value.flow)
const returnTemp = ref(marketTemps.value.return)
const roomTemp = ref(marketTemps.value.room)
function applyStartTemps() {
  const b = tempBounds.value
  flowTemp.value = clampTemp(marketTemps.value.flow, b.flowMin, b.flowMax)
  returnTemp.value = clampTemp(marketTemps.value.return, b.returnMin, b.returnMax)
  roomTemp.value = clampTemp(marketTemps.value.room, b.roomMin, b.roomMax)
}
watch(marketTemps, applyStartTemps)
const targetWattage = ref(1000)
const tolerancePct = ref(15)
const calculationKind = ref('')

// Same per-category defaults as the list view. The matrix has a single symmetric tolerance,
// so the wider side of the configured band is used. Target follows the product median when the
// API supplies one (electric products).
const defaultsOverride = readDefaultsOverrideFromLocation()
const appliedDefaultsKey = ref('')
const productMedianWattage = ref(null)
function applyCalculatorDefaults([, , median] = [], [, , previousMedian] = []) {
  const defaults = resolveCalculatorDefaults({
    category: props.category,
    calculation: calculationKind.value,
    override: defaultsOverride,
  })
  const keyChanged = defaults.key !== appliedDefaultsKey.value
  const medianChanged = median !== previousMedian
  if (keyChanged) {
    appliedDefaultsKey.value = defaults.key
    tolerancePct.value = Math.max(
      1,
      Math.max(Math.abs(defaults.toleranceMin), Math.abs(defaults.toleranceMax))
    )
  }
  if (keyChanged || medianChanged) {
    targetWattage.value = resolveTargetWattage({
      defaults,
      productMedianWattage: median,
      override: defaultsOverride,
    })
  }
}
watch(
  () => [props.category, calculationKind.value, productMedianWattage.value],
  applyCalculatorDefaults,
  { immediate: true }
)

const allItems = ref([])
const typeOptions = ref([])
// Accumulated across fetches so dropdown options survive dimension switches
const knownHeights = ref([])
const knownLengths = ref([])
const isLoading = ref(false)
const loadError = ref('')
const hasCalculated = ref(false)

const s = computed(() => props.strings?.matrixCalc ?? {})

// Same translated labels as the list view; the matrixCalc copies are untranslated English
const targetOutputLabel = computed(() => props.strings?.calc?.requiredWatts ?? 'Required Watts')
const toleranceLabel = computed(() => props.strings?.calc?.tolerance ?? 'Tolerance')
const calculateLabel = computed(
  () => props.strings?.calc?.actionButton ?? s.value.calculateButton ?? 'Calculate'
)
const calculatingLabel = computed(
  () => props.strings?.calc?.calculating ?? s.value.loading ?? 'Loading...'
)

function toTypeId(item) {
  if (item == null) return null
  if (typeof item === 'string' || typeof item === 'number') return String(item)
  return String(item.id ?? item.code ?? item.value ?? item.type ?? item.name ?? '')
}

function accumulateDimensions(items) {
  const heights = items.map((i) => i.heightMm).filter(Number.isFinite)
  const lengths = items.map((i) => i.lengthMm ?? i.widthMm).filter(Number.isFinite)
  knownHeights.value = [...new Set([...knownHeights.value, ...heights])].sort((a, b) => a - b)
  knownLengths.value = [...new Set([...knownLengths.value, ...lengths])].sort((a, b) => a - b)
}

watch(
  () => [props.productId, props.market],
  async ([productId, market]) => {
    if (!productId || !market) return
    allItems.value = []
    hasCalculated.value = false
    typeOptions.value = []
    knownHeights.value = []
    knownLengths.value = []
    lockedValue.value = null
    loadError.value = ''
    tempBounds.value = { ...DEFAULT_TEMP_BOUNDS }
    try {
      const data = await fetchPanelTypes(market, productId, props.lang)
      const list = Array.isArray(data) ? data : (data?.types ?? [])
      typeOptions.value = list.map(toTypeId).filter(Boolean)
      calculationKind.value = typeof data?.calculation === 'string' ? data.calculation : ''
      productMedianWattage.value = Number.isFinite(data?.medianWattage) ? data.medianWattage : null
      const heating = data?.heating
      if (heating) {
        const bound = (key) =>
          Number.isFinite(heating[key]) ? heating[key] : DEFAULT_TEMP_BOUNDS[key]
        tempBounds.value = Object.fromEntries(
          Object.keys(DEFAULT_TEMP_BOUNDS).map((k) => [k, bound(k)])
        )
      }
      applyStartTemps()
      if (lockDimension.value === 'type' && typeOptions.value.length) {
        lockedValue.value = typeOptions.value[0]
      }
    } catch {
      // non-fatal: user can still calculate
      productMedianWattage.value = null
    }
  },
  { immediate: true }
)

// Unique values from the current API response (used for matrix axes only, not for lock options)
const uniqueTypes = computed(() =>
  [...new Set(allItems.value.map((i) => String(i.type ?? '')))].sort()
)
const uniqueHeights = computed(() =>
  [...new Set(allItems.value.map((i) => i.heightMm))].sort((a, b) => a - b)
)
const uniqueLengths = computed(() =>
  [...new Set(allItems.value.map((i) => i.lengthMm ?? i.widthMm))].sort((a, b) => a - b)
)

// Lock value dropdown options: types always from fetchPanelTypes; heights/lengths accumulated
const lockValueOptions = computed(() => {
  if (lockDimension.value === 'type') return typeOptions.value
  if (lockDimension.value === 'height') return knownHeights.value
  return knownLengths.value
})

watch(lockDimension, async (dim) => {
  const opts = lockValueOptions.value
  if (opts.length) {
    lockedValue.value = opts[0]
    // Auto-refetch if we already have a calculated result
    if (hasCalculated.value) calculate()
  } else if (typeOptions.value.length) {
    // No known heights/lengths yet — run a discovery call filtered by first type
    // so we don't load the full catalog
    await discoverDimensions()
  }
})

// Auto-refetch when lock value changes (after first calculate)
watch(lockedValue, (val) => {
  if (hasCalculated.value && val != null) calculate()
})

// Bootstrap height/length options by fetching one type's worth of items
async function discoverDimensions() {
  const firstType = typeOptions.value[0]
  if (!firstType || !props.productId || !props.market) return
  try {
    const data = await fetchPanelMatches(
      props.market,
      props.productId,
      {
        types: [firstType],
        targetWattage: 1000,
        flowTemp: flowTemp.value,
        returnTemp: returnTemp.value,
        roomTemp: roomTemp.value,
        heightMin: 0,
        heightMax: 99999,
        lengthMin: 0,
        lengthMax: 99999,
        toleranceMin: -100,
        toleranceMax: 9999900,
      },
      props.lang
    )
    const items = (Array.isArray(data) ? data : []).flatMap((g) => g.items ?? [])
    accumulateDimensions(items)
    const opts = lockValueOptions.value
    if (opts.length && lockedValue.value == null) lockedValue.value = opts[0]
  } catch {
    // ignore — user can still type in a value or click Calculate
  }
}

const AXIS_MAP = {
  type: { rowKey: 'length', colKey: 'height' },
  height: { rowKey: 'length', colKey: 'type' },
  length: { rowKey: 'height', colKey: 'type' },
}

function getItemDimVal(item, key) {
  if (key === 'height') return item.heightMm
  if (key === 'length') return item.lengthMm ?? item.widthMm
  return String(item.type ?? '')
}

// Display helper: shows the noType label for type values that are empty/missing
function displayDimVal(key, val) {
  if (key === 'type' && (val === '' || val == null)) return s.value.noType ?? '—'
  return val
}

// Axes are derived from the current API response (already filtered by API)
const axisConfig = computed(() => {
  const { rowKey, colKey } = AXIS_MAP[lockDimension.value]
  const rowValues =
    rowKey === 'height'
      ? uniqueHeights.value
      : rowKey === 'length'
        ? uniqueLengths.value
        : uniqueTypes.value
  const colValues =
    colKey === 'type'
      ? uniqueTypes.value
      : colKey === 'height'
        ? uniqueHeights.value
        : uniqueLengths.value
  return { rowKey, colKey, rowValues, colValues }
})

// API already filtered to the locked dimension — no client-side filter needed
const matrixMap = computed(() => {
  const { rowKey, colKey } = AXIS_MAP[lockDimension.value]
  const map = new Map()
  for (const item of allItems.value) {
    const r = getItemDimVal(item, rowKey)
    const c = getItemDimVal(item, colKey)
    map.set(`${r}__${c}`, item)
  }
  return map
})

const axisLabels = computed(() => ({
  height: s.value.axisHeight ?? 'Height (mm)',
  length: s.value.axisLength ?? 'Length (mm)',
  type: s.value.axisType ?? 'Type',
}))

const visibleRowValues = computed(() => {
  const { rowValues, colValues } = axisConfig.value
  if (!hideOutOfRange.value) return rowValues
  return rowValues.filter((row) =>
    colValues.some((col) => isMatch(matrixMap.value.get(`${row}__${col}`)))
  )
})
const visibleColValues = computed(() => {
  const { rowValues, colValues } = axisConfig.value
  if (!hideOutOfRange.value) return colValues
  return colValues.filter((col) =>
    rowValues.some((row) => isMatch(matrixMap.value.get(`${row}__${col}`)))
  )
})

// The item to show in a cell, or undefined when the cell is empty or hidden by the range filter
function cellItem(row, col) {
  const item = matrixMap.value.get(`${row}__${col}`)
  return hideOutOfRange.value && !isMatch(item) ? undefined : item
}

const rowAxisLabel = computed(() => axisLabels.value[axisConfig.value.rowKey])
const colAxisLabel = computed(() => axisLabels.value[axisConfig.value.colKey])

const lockDimensionLabel = computed(() => {
  const key = lockDimension.value
  return key === 'type'
    ? (s.value.lockType ?? 'Type')
    : key === 'height'
      ? (s.value.lockHeight ?? 'Height')
      : (s.value.lockLength ?? 'Length')
})

// 'all' shows every cell; 'matches' blanks cells outside the tolerance band and drops rows and
// columns that have no match at all
const displayMode = ref('all')
const hideOutOfRange = computed(() => displayMode.value === 'matches')

function isMatch(item) {
  if (!targetWattage.value || !item?.calculatedWattage) return false
  const low = targetWattage.value * (1 - tolerancePct.value / 100)
  const high = targetWattage.value * (1 + tolerancePct.value / 100)
  return item.calculatedWattage >= low && item.calculatedWattage <= high
}

function buildPayload() {
  const dim = lockDimension.value
  const val = lockedValue.value
  return {
    types: dim === 'type' && val != null && val !== '' ? [String(val)] : [],
    targetWattage: Math.max(1, targetWattage.value),
    flowTemp: flowTemp.value,
    returnTemp: returnTemp.value,
    roomTemp: roomTemp.value,
    heightMin: dim === 'height' && val != null ? Number(val) : 0,
    heightMax: dim === 'height' && val != null ? Number(val) : 99999,
    lengthMin: dim === 'length' && val != null ? Number(val) : 0,
    lengthMax: dim === 'length' && val != null ? Number(val) : 99999,
    toleranceMin: -100,
    toleranceMax: 9999900,
  }
}

async function calculate() {
  if (!props.productId || !props.market) return
  isLoading.value = true
  loadError.value = ''
  try {
    const data = await fetchPanelMatches(props.market, props.productId, buildPayload(), props.lang)
    const groups = Array.isArray(data) ? data : []
    allItems.value = groups.flatMap((g) => g.items ?? [])
    accumulateDimensions(allItems.value)
    hasCalculated.value = true
  } catch (err) {
    loadError.value = err?.message ?? props.strings?.calc?.errorMatch ?? 'Calculation error.'
    allItems.value = []
  } finally {
    isLoading.value = false
  }
}

const isExporting = ref(false)

// Snapshot of the table exactly as rendered, including which cells are highlighted
function buildExportModel() {
  const { rowKey, colKey } = axisConfig.value
  const rowValues = visibleRowValues.value
  const colValues = visibleColValues.value
  return {
    market: props.market,
    title: props.product?.name ?? s.value.title ?? 'Matrix',
    summary: [
      `${lockDimensionLabel.value}: ${displayDimVal(lockDimension.value, lockedValue.value)}`,
      `${props.strings?.calc?.flowTemp ?? 'Flow temp C°'}: ${flowTemp.value}°C, ` +
        `${props.strings?.calc?.returnTemp ?? 'Return temp C°'}: ${returnTemp.value}°C, ` +
        `${props.strings?.calc?.roomTemp ?? 'Room temp C°'}: ${roomTemp.value}°C`,
      `${targetOutputLabel.value}: ${targetWattage.value} W, ` +
        `${toleranceLabel.value}: ${tolerancePct.value}%`,
    ],
    rowAxisLabel: axisLabels.value[rowKey],
    colAxisLabel: axisLabels.value[colKey],
    colHeaders: colValues.map((c) => String(displayDimVal(colKey, c))),
    rows: rowValues.map((row) => ({
      header: String(displayDimVal(rowKey, row)),
      cells: colValues.map((col) => {
        const item = cellItem(row, col)
        return item
          ? {
              value: Math.round(item.calculatedWattage),
              text: String(Math.round(item.calculatedWattage)),
              match: isMatch(item),
            }
          : { value: null, text: s.value.noItem ?? '—', match: false }
      }),
    })),
  }
}

async function runExport(kind) {
  isExporting.value = true
  try {
    const { exportMatrixToExcel, exportMatrixToPdf, printMatrix } =
      await import('../lib/matrix-export.js')
    const model = buildExportModel()
    const action = { excel: exportMatrixToExcel, pdf: exportMatrixToPdf, print: printMatrix }[kind]
    await action(model)
  } catch (err) {
    loadError.value = err?.message ?? 'Export error.'
  } finally {
    isExporting.value = false
  }
}
</script>

<template>
  <div class="matrix-calculator" :class="{ 'matrix-calculator--standalone': !isEmbedded }">
    <div class="matrix-controls">
      <div class="matrix-temps">
        <div class="matrix-field">
          <p class="matrix-label">{{ strings?.calc?.flowTemp ?? 'Flow temp C°' }}</p>
          <ValueSlider
            v-model="flowTemp"
            :label="strings?.slider?.value ?? 'Value'"
            :min="tempBounds.flowMin"
            :max="tempBounds.flowMax"
            :step="1"
            unit="°C"
          />
        </div>
        <div class="matrix-field">
          <p class="matrix-label">{{ strings?.calc?.returnTemp ?? 'Return temp C°' }}</p>
          <ValueSlider
            v-model="returnTemp"
            :label="strings?.slider?.value ?? 'Value'"
            :min="tempBounds.returnMin"
            :max="tempBounds.returnMax"
            :step="1"
            unit="°C"
          />
        </div>
        <div class="matrix-field">
          <p class="matrix-label">{{ strings?.calc?.roomTemp ?? 'Room temp C°' }}</p>
          <ValueSlider
            v-model="roomTemp"
            :label="strings?.slider?.value ?? 'Value'"
            :min="tempBounds.roomMin"
            :max="tempBounds.roomMax"
            :step="1"
            unit="°C"
          />
        </div>
      </div>

      <div class="matrix-target">
        <div class="matrix-field">
          <p class="matrix-label">{{ targetOutputLabel }}</p>
          <ValueSlider
            v-model="targetWattage"
            :label="strings?.slider?.value ?? 'Value'"
            :min="0"
            :max="10000"
            :step="50"
            unit="W"
          />
        </div>
        <div class="matrix-field">
          <p class="matrix-label">{{ toleranceLabel }}</p>
          <ValueSlider
            v-model="tolerancePct"
            :label="strings?.slider?.value ?? 'Value'"
            :min="1"
            :max="30"
            :step="1"
            unit="%"
          />
        </div>
      </div>

      <div class="matrix-lock-row">
        <label class="matrix-lock-select">
          <span class="lock-label">{{ s.lockLabel ?? 'Lock dimension' }}</span>
          <select v-model="lockDimension" class="matrix-select">
            <option value="type">{{ s.lockType ?? 'Type' }}</option>
            <option value="height">{{ s.lockHeight ?? 'Height' }}</option>
            <option value="length">{{ s.lockLength ?? 'Length' }}</option>
          </select>
        </label>
        <label class="matrix-lock-select" v-if="lockValueOptions.length">
          <span class="lock-label">{{ lockDimensionLabel }}</span>
          <select v-model="lockedValue" class="matrix-select">
            <option v-for="opt in lockValueOptions" :key="opt" :value="opt">
              {{ displayDimVal(lockDimension, opt) }}
            </option>
          </select>
        </label>
        <ActionButton @click="calculate" :disabled="isLoading || !productId">
          {{ isLoading ? calculatingLabel : calculateLabel }}
        </ActionButton>

        <div v-if="hasCalculated && !isLoading && !loadError" class="matrix-lock-actions">
          <fieldset class="matrix-display">
            <legend class="matrix-label">{{ s.displayLabel ?? 'Display' }}</legend>
            <label class="matrix-radio">
              <input v-model="displayMode" type="radio" name="matrix-display" value="all" />
              <span>{{ s.showAll ?? 'Show all' }}</span>
            </label>
            <label class="matrix-radio">
              <input v-model="displayMode" type="radio" name="matrix-display" value="matches" />
              <span>{{ s.hideOutOfRange ?? 'Hide items out of range' }}</span>
            </label>
          </fieldset>

          <div class="matrix-export-row">
            <ActionButton variant="secondary" :disabled="isExporting" @click="runExport('excel')">
              {{ s.exportExcel ?? 'Export as Excel' }}
            </ActionButton>
            <ActionButton variant="secondary" :disabled="isExporting" @click="runExport('pdf')">
              {{ s.exportPdf ?? 'Export as PDF' }}
            </ActionButton>
            <ActionButton variant="secondary" :disabled="isExporting" @click="runExport('print')">
              {{ s.print ?? 'Print' }}
            </ActionButton>
          </div>
        </div>
      </div>
    </div>

    <p v-if="loadError" class="matrix-error">{{ loadError }}</p>

    <p
      v-if="hasCalculated && !isLoading && !loadError && !visibleRowValues.length"
      class="matrix-prompt"
    >
      {{ strings?.calc?.emptyResults ?? '—' }}
    </p>

    <div v-else-if="hasCalculated && !isLoading && !loadError" class="matrix-table-wrapper">
      <table class="matrix-table">
        <thead>
          <tr class="matrix-axis-header">
            <th class="matrix-corner-cell">{{ rowAxisLabel }}</th>
            <th :colspan="visibleColValues.length" class="matrix-col-axis">
              {{ colAxisLabel }}
            </th>
          </tr>
          <tr>
            <th class="matrix-row-label"></th>
            <th v-for="col in visibleColValues" :key="col" class="matrix-col-header">
              {{ displayDimVal(axisConfig.colKey, col) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in visibleRowValues" :key="row">
            <td class="matrix-row-header">{{ displayDimVal(axisConfig.rowKey, row) }}</td>
            <td
              v-for="col in visibleColValues"
              :key="col"
              :class="{ 'cell--match': isMatch(cellItem(row, col)) }"
              class="matrix-cell"
            >
              <template v-if="cellItem(row, col)">
                {{ Math.round(cellItem(row, col).calculatedWattage) }}
              </template>
              <span v-else class="cell--empty">{{ s.noItem ?? '—' }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p v-else-if="!hasCalculated && !isLoading" class="matrix-prompt">
      {{ s.noData ?? 'Select a radiator and click Calculate to view the matrix.' }}
    </p>
  </div>
</template>

<style scoped>
.matrix-calculator {
  box-sizing: border-box;
  max-width: 1200px;
  width: 100%;
}

.matrix-calculator--standalone {
  margin: clamp(24px, 5vw, 64px) auto;
  padding: 0 var(--space-24) var(--space-64);
}

.matrix-controls {
  display: flex;
  flex-direction: column;
  gap: var(--space-20, 20px);
  margin-bottom: var(--space-32, 32px);
}

.matrix-temps {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-24, 24px);
}

.matrix-field {
  border-top: 1px solid var(--semantic-border-muted, #a6b4b6);
  padding-top: var(--space-16, 16px);
}

.matrix-label {
  margin: 0 0 8px 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--semantic-text-default, #00292e);
}

.matrix-temps > * {
  flex: 1 1 160px;
}

.matrix-target {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-24, 24px);
}

.matrix-target > * {
  flex: 1 1 200px;
}

.matrix-lock-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-16, 16px);
}

.matrix-lock-select {
  display: grid;
  gap: 6px;
  min-width: 160px;
}

.lock-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--semantic-text-strong, #4e6b6e);
}

/* Same look as the selects in ExistingRadiator and ModalDrawer */
.matrix-select {
  width: 100%;
  height: 36px;
  padding: 8px 40px 8px 10px;
  border: 1px solid var(--semantic-border-muted);
  border-radius: 2px;
  background-color: var(--semantic-bg-default);
  color: var(--semantic-text-default);
  font-size: 0.85rem;
  appearance: none;
  outline: none;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M7 10l5 5 5-5' fill='none' stroke='%23888' stroke-width='2'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 18px 18px;
}

.matrix-error {
  color: #c0392b;
  margin: var(--space-16, 16px) 0;
}

.matrix-prompt {
  color: var(--semantic-text-muted, #a6b4b6);
  margin: var(--space-32, 32px) 0;
}

.matrix-display {
  border: none;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-8, 8px) var(--space-24, 24px);
  margin: 0;
  padding: 0;
}

.matrix-lock-actions {
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-16, 16px) var(--space-24, 24px);
  justify-content: flex-end;
  margin-left: auto;
}

.matrix-display .matrix-label {
  float: left;
  margin: 0;
  padding: 0;
  width: 100%;
}

.matrix-radio {
  align-items: center;
  color: var(--semantic-text-default, #00292e);
  cursor: pointer;
  display: inline-flex;
  font-size: 0.9rem;
  gap: 8px;
}

.matrix-radio input {
  accent-color: var(--color-petrol-70, #3e909f);
  cursor: pointer;
  margin: 0;
}

.matrix-export-row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-12, 12px);
}

.matrix-table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.matrix-table {
  border-collapse: collapse;
  font-size: 0.85rem;
  min-width: 400px;
  width: 100%;
}

.matrix-table th,
.matrix-table td {
  border: 1px solid var(--semantic-border-subtle, #edf0f1);
  padding: 6px 10px;
  text-align: center;
  white-space: nowrap;
}

.matrix-axis-header th {
  background: var(--semantic-bg-petrol-subtle, #edf0f1);
  color: var(--color-petrol-100, #00292e);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.matrix-corner-cell {
  text-align: left;
}

/* First column keeps a fixed width; the remaining columns share what is left */
.matrix-table th:first-child,
.matrix-table td:first-child {
  box-sizing: border-box;
  max-width: 130px;
  min-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 130px;
}

.matrix-col-axis {
  text-align: left;
  border-left: none;
}

.matrix-col-header {
  background: var(--semantic-table-strong, #f3f3f6);
  color: var(--color-petrol-100, #00292e);
  font-weight: 600;
  min-width: 60px;
}

.matrix-row-label {
  background: var(--semantic-table-strong, #f3f3f6);
}

.matrix-row-header {
  background: var(--semantic-table-light, #f8f8fa);
  color: var(--color-petrol-100, #00292e);
  font-weight: 600;
  text-align: left;
  padding-left: 12px;
}

.matrix-cell {
  color: var(--semantic-text-default, #00292e);
  transition: background 0.1s;
}

.matrix-cell.cell--match {
  background: var(--semantic-bg-blue-subtle, #ecf4f5);
  color: var(--semantic-bg-blue-heavy, #3e909f);
  font-weight: 700;
  outline: 1px solid var(--semantic-bg-blue-heavy, #3e909f);
  outline-offset: -1px;
}

.cell--empty {
  color: var(--semantic-text-muted, #a6b4b6);
}

@media (max-width: 640px) {
  .matrix-calculator {
    padding: 0 var(--space-16) var(--space-32);
  }
}
</style>
